/**
 * Event Tracking Domain Models
 * Domain layer - Business entities for analytics events
 */

/**
 * Event Properties
 * Generic properties that can be attached to any event
 */
export interface EventProperties {
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Event Context
 * Additional context information about the event
 */
export interface EventContext {
  page?: string;
  section?: string;
  component?: string;
  userId?: string;
  sessionId?: string;
  timestamp?: number;
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Event
 * Domain model for analytics events
 * Represents a single event that can be tracked across multiple providers
 */
export class Event {
  constructor(
    public readonly name: string,
    public readonly properties: EventProperties = {},
    public readonly context: EventContext = {}
  ) {
    // Ensure timestamp is set if not provided
    if (!this.context.timestamp) {
      this.context.timestamp = Date.now();
    }
  }

  /**
   * Create a copy of the event with additional properties
   */
  withProperties(additionalProperties: EventProperties): Event {
    return new Event(
      this.name,
      { ...this.properties, ...additionalProperties },
      this.context
    );
  }

  /**
   * Create a copy of the event with additional context
   */
  withContext(additionalContext: EventContext): Event {
    return new Event(
      this.name,
      this.properties,
      { ...this.context, ...additionalContext }
    );
  }

  /**
   * Convert event to plain object for serialization
   */
  toPlainObject(): Record<string, unknown> {
    return {
      name: this.name,
      properties: this.properties,
      context: this.context,
    };
  }
}

